import {
  Container,
  Button,
  Text,
  Center,
  Card,
  Box,
  LoadingOverlay
} from "@mantine/core";
import React, { useContext, useEffect, useState } from "react";
import { BannerConfiguration } from "./consent/BannerConfiguration";
import { ConsentService } from "../../services/consentService";
import { showNotification } from '@mantine/notifications';
import { ConfigContext } from "../../Context/configContext";
import { useTitle } from "../hooks/useTitle";
import { themesConfig } from "@/utils/utils";
import { IconPlus } from "@tabler/icons-react";
import { Wizard } from './consentV2/Wizard';
import noDataImage from '../../images/no-domain.jpg';
import { useTranslation } from 'react-i18next';

const tempObj = {
  language: 'en',
  domainId: '',
  domainUrl: '',
  geoLocations: [],
  consentExpire: '',
  styles: '',
  locations: []
};

function ConsentBannerPage(props: any): JSX.Element {
  const { t } = useTranslation();
  const { config } = useContext(ConfigContext);
  const [domainId, setDomainId] = useState<any | null>(props?.currentConsentData?.domainId || localStorage.getItem('activeDomainId'));
  const [domainName, setDomainName] = useState<any | null>(localStorage.getItem('activeDomain'));
  const [consentConfigRequest, setConsentConfigRequest] = useState<any>(tempObj);
  const [configuredTheme, setConfiguredTheme] = useState<any | null>(null);
  const [showWizard, setShowWizard] = useState(false); // ➕ Add New Site wizard
  const [isLoading, setIsLoading] = useState(true);

  const consentService = new ConsentService(config!);

  useTitle(t('consentBanner.manageConsentBanner'));

  useEffect(() => {
    if (props.currentConsentData?.domainId) {
      setDomainId(props.currentConsentData.domainId);
    }
  }, [props.currentConsentData]);

  useEffect(() => {
    if (domainId) {
      getConsentById(domainId);
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  const getConsentById = async (id: any) => {
    try {
      setIsLoading(true);
      const response = await consentService.getConsentById(id);
      if (response?.data) {
        setConsentConfigRequest(response.data);
        let themeData = response.data?.theme?.theme ? { ...response.data } : { theme: { theme: themesConfig[0] } };
        setConfiguredTheme(themeData);
        localStorage.setItem("activeDomain", response.data.domainUrl);
      }
    } catch (error) {
      console.error("Error fetching consent data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConsentData = (fieldValue: any) => {
    const payload: any = {
      consentExpire: consentConfigRequest.consentExpire,
      geoLocations: ['Asia-Pacific'],
      domainUrl: consentConfigRequest.domainUrl,
      language: consentConfigRequest.language,
      subdomainConsentSharing: true,
      consentLog: true,
      styles: fieldValue
    };

    const response = consentService.updateConsent(domainId, payload);
    response.then((res) => {
      if (res) {
        setDomainId(res?.id);
        setDomainName(res?.domainUrl);
        showNotification({ color: "green", message: t('consentBanner.updateSuccess') });
      }
    });
  };

  return (
    <Container fluid>
      {isLoading ? (
        <LoadingOverlay visible={true} zIndex={1000}   overlayProps={{ radius: 'sm', blur: 2 }} loaderProps={{ color: 'green', type: 'bars' }}/>
      ) : domainId && configuredTheme ? (
        <BannerConfiguration
          consentData={handleConsentData}
          consentConfigData={configuredTheme}
          domainId={domainId}
          domainName={domainName}
        />
      ) : (
        <Box mt={20}>
          <Card>
            <Center 
                style={{ 
                  flexDirection: 'column',
                  gap: '24px',
                  padding: '60px 20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  width: '100%',
                  minHeight: '300px'
                }}
              >
                <img 
                  src={noDataImage} 
                  alt="No Data Available"
                  style={{ 
                    width: '280px',
                    height: 'auto',
                    opacity: 0.8,
                    filter: 'grayscale(20%)',
                    marginBottom: '8px'
                  }} 
                />
                <div style={{ textAlign: 'center' }}>
                  <Text size="xl" fw={500} mb={8}>{t('consentBanner.noDomainsFound')}</Text>
                  <Text c="dimmed" size="sm">
                    {t('consentBanner.noDomainsDescription')}
                  </Text>
                  <Button
                    onClick={() => setShowWizard(true)}
                    color="blue"
                    size="sm"
                    mt={20}
                    leftSection={<IconPlus size={16} />}
                  >
                    {t('consentBanner.addNewSite')}
                  </Button>
                  {showWizard && (
                    <Wizard handleRefresh={() => window.location.reload()} />
                  )}
                </div>
            </Center>
          </Card>
        </Box>
      )}
    </Container>
  );
}

export { ConsentBannerPage };
